import React, { useContext, useState } from "react";
import { Link, navigate } from "gatsby";
import { Box, Typography, Avatar, useTheme, Badge } from "@mui/material";

import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";

import { findConfigFromType, findQueryParameter } from "gatsby-frontend";

import { SiteContext } from "gatsby-frontend/src/Site";

import { Date, Tags, DownloadModal, Image, Video, Underline } from "./../../";

function Icon({ type }) {
	return type.includes("media") ? <DownloadIcon /> : <DescriptionIcon />;
}

function Title({
	slug,
	title,
	external,
	download,
	underline,
	component = "h3",
	variant = "h4",
	linkColor,
	singleLine = false,
	title_append,
	sx = {},
}) {
	// const theme = useTheme();
	const [uuid, setUuid] = useState(null);
	const handleModal = (_uuid = null) => setUuid(_uuid);
	const { location } = useContext(SiteContext);

	let attrs = {};
	if (slug) {
		attrs = {
			component: external ? "a" : Link,
			target: external ? "_blank" : "_self",
			sx: {
				display: "block",
				textDecoration: "none",
				color: linkColor.main,
				"&:hover, &:active, &:focus": {
					color: linkColor.hover,
					textDecoration: "underline",
				},
			},
		};
		if (external) {
			attrs.href = slug;
		} else {
			attrs.to = slug;
		}
		if (download && slug.includes("download?media")) {
			attrs.onClick = (e) => {
				e.preventDefault();
				let _search = slug.includes("?") ? `?${slug.split("?")[1]}` : null;
				if (handleModal && _search) {
					let _uuid = findQueryParameter(_search, "media");
					handleModal(_uuid);
				} else {
					let referrer = location.search
						? `${location.pathname}${location.search}`
						: location.pathname;
					navigate(slug, { state: { referrer } });
				}
			};
		}
	} else {
		attrs = {
			component: "span",
			sx: {
				display: "block",
				textDecoration: "none",
			},
		};
	}
	if (singleLine) {
		attrs.sx.whiteSpace = "nowrap";
		attrs.sx.overflow = "hidden";
		attrs.sx.textOverflow = "ellipsis";
	}

	return (
		<>
			<Typography
				variant={variant}
				component={component}
				className="excerpt-title"
				sx={{ ...sx }}
			>
				<Box {...attrs}>
					<span dangerouslySetInnerHTML={{ __html: title }}></span>
					{title_append && <small>{`, ${title_append}`}</small>}
				</Box>
				{underline && <Underline color={underline} />}
			</Typography>
			<DownloadModal uuid={uuid} setUuid={setUuid} />
		</>
	);
}

function Excerpt(props) {
	let _props = { ...props };

	const theme = useTheme();
	const { configs, themeConfig, typeDefinitions } = useContext(SiteContext);
	const config = configs ? findConfigFromType(props.type, configs) : null;

	let hiddenInfo =
		config &&
		config?.options?.excerptHide &&
		Array.isArray(config?.options?.excerptHide)
			? config?.options?.excerptHide
			: [];

	if (!_props.hasSearchQuery) {
		let excerptOverwrite = {};
		if (_props.global && _props.subtitle) {
			excerptOverwrite.summary = "subtitle";
		}
		if (config && config.options && config.options.excerptOverwrite) {
			excerptOverwrite = {
				...excerptOverwrite,
				...config.options.excerptOverwrite,
			};
		}

		for (const [excerpt_overwrite_from, excerpt_overwrite_to] of Object.entries(
			excerptOverwrite
		)) {
			let new_val = _props[excerpt_overwrite_from];
			if (_props[excerpt_overwrite_to]) {
				new_val = _props[excerpt_overwrite_to];
			}
			_props[excerpt_overwrite_to] = new_val;
			hiddenInfo.push(excerpt_overwrite_from);
		}
	}

	const {
		type,
		title,
		subtitle,
		slug,
		external,
		download,
		summary,
		image,
		video,
		orientation,
		mode,
		dates,
		tags,
		flush,
		isSidebar,
		headingComponent = "h3",
		global = false,
		dateFormat = "D",
		datePosition = "after",
		thumbnailPosition = "before",
		thumbnailSize = "full",
		thumbnailAlign = "left",
		thumbOption,
		title_append,
		extra_attributes,
		newBadge,
		categories,
		hasSearchQuery = false,
	} = _props;

	let _image = image;
	if (thumbOption === "force" && !_image) {
		_image = { src: "/default.jpg", alt: "" };
	}

	const avatar = _image
		? { alt: _image.alt, src: _image.url ? _image.url : _image.src }
		: {};
	const avatarStyle = config?.options?.avatarStyle || "circle";

	const linkColor = theme.palette.excerptLink
		? theme.palette.excerptLink
		: theme.palette.link;

	const excerptLearnMore =
		(theme.customOptions?.excerptLearnMore && !global && slug) || null;

	const showType = theme?.customOptions?.showType;
	const typeName = type.split("--")[1];
	const matchingType = typeDefinitions.find(
		(definition) => definition.bundle === typeName
	);

	let clss = mode === "list" ? "excerpt-list-item" : "excerpt-card";
	clss += ` date-position-${datePosition}  thumbnail-position-${thumbnailPosition} thumbnail-size-${thumbnailSize} kind-${
		type.includes("media") ? "media" : "node"
	} type-${type} thumbnail-orientation-${orientation} thumbnail-align-${thumbnailAlign}`;
	if (flush) clss += " flush";
	if (isSidebar) clss += " is-sidebar";

	if (global) clss += " is-global";
	const underline =
		!global && theme.palette.excerptUnderline
			? theme.palette.excerptUnderline
			: null;

	if (!mode && config) {
		if (config?.options?.excerpt?.mode) {
			mode = config.options.excerpt.mode;
		}
	}

	const is_vcard =
		!global && ((config && config?.options?.vCard) || mode === "vcard")
			? true
			: false;

	const vcard_align = is_vcard ? config?.options?.vCardAlign || "center" : null;

	if (is_vcard) clss += " is-vcard";
	if (vcard_align) clss += ` vcard-align-${vcard_align}`;

	if (
		global &&
		themeConfig?.globalSearch?.excerptHide &&
		Array.isArray(themeConfig.globalSearch.excerptHide)
	) {
		hiddenInfo = [...hiddenInfo, ...themeConfig.globalSearch.excerptHide];
	}

	if (config && config.type === "node--page") {
		hiddenInfo.push("date");
	}

	clss += ` ${hiddenInfo.map((h) => `hide-${h}`).join(" ")}`;

	function Dt() {
		return dates && !hiddenInfo.includes("date") ? (
			<Date dates={dates} format={dateFormat} />
		) : (
			<></>
		);
	}

	function Thumb({ size = "full", mode = "grid" }) {
		if (hiddenInfo.includes("thumbnail")) return <></>;
		if (is_vcard) {
			return (
				<Avatar
					{...avatar}
					sx={{
						width: 80,
						height: 80,
						margin: `0 auto`,
						...(avatarStyle === "square" && { borderRadius: 0 }),
					}}
					component={slug ? Link : "span"}
					to={slug}
				>
					<Icon type={type} />
				</Avatar>
			);
		} else if (type.includes("media--")) {
			return (
				<Box
					component={slug ? Link : "span"}
					sx={{
						marginRight: theme.spacing(1.5),
						display: "flex",
						color: linkColor.main,
						"&:hover, &:active, &:focus": {
							color: linkColor.hover,
						},
					}}
					to={slug}
				>
					<Icon type={type} />
				</Box>
			);
		} else {
			return video || _image ? (
				<Box
					className="thumbnail-wrapper"
					sx={{
						...(mode === "list" && { "& .image": { marginTop: 0 } }),
						...(size === "md" && { width: "220px", minWidth: "220px" }),
						...(size === "sm" && { width: "160px", minWidth: "160px" }),
						...(size === "sm" &&
							(orientation === "square" || orientation === "rounded") && {
								width: "100px",
								minWidth: "100px",
							}),
					}}
				>
					{video ? (
						<Video
							thumbnail={video.thumbnail}
							url={video.url}
							thumbnailSize={size}
							orientation={orientation}
							isSidebar={isSidebar}
						/>
					) : (
						<Image
							src={_image.url ? _image.url : _image.src}
							orientation={orientation}
							href={slug}
							alt={_image.alt}
							size={size}
							isSidebar={isSidebar}
						/>
					)}
				</Box>
			) : (
				<></>
			);
		}
	}

	const formatCategories = (categories) => {
		if (!categories) {
			return "";
		}

		return categories
			.flatMap((category) =>
				category.items.map((item) =>
					item.title.trim().toLowerCase().replace(/ /g, "-")
				)
			)
			.join(" ");
	};

	return (
		<Box
			className={clss}
			{...(categories
				? { "publication-category": formatCategories(categories) }
				: {})}
			sx={{
				height: mode === "list" ? "auto" : "100%",
				...(flush && {
					boxShadow: "none",
					background: "transparent",
				}),
				"& .excerpt-content": {
					"& > *:not(:first-child)": {
						marginTop: theme.spacing(1),
					},
				},
				"&.is-vcard:not(.vcard-align-left)": {
					"& .excerpt-content": {
						paddingTop: theme.spacing(1.5),
						textAlign: "center",
						"& .underline": {
							marginLeft: "auto",
							marginRight: "auto",
						},
					},
				},
				"&.flush": {
					"& .excerpt-content": {
						"& time": {
							lineHeight: 1.4,
						},
					},
					"&.date-position-before.thumbnail-position-after": {
						"& .excerpt-content > *:not(:first-child)": {
							marginTop: "16px",
						},
					},
				},
				"&.excerpt-list-item": {
					boxShadow: "none",
					background: "transparent",
					padding: theme.spacing(0, 0, 3),
					"& .excerpt-content": {
						paddingX: 0,
						"& > *:not(:first-child)": {
							//marginTop: theme.spacing(0.25),
						},
					},
					"&.thumbnail-align-right:not(.thumbnail-size-full)": {
						"& .thumbnail-wrapper": {
							marginRight: 0,
							marginLeft: theme.spacing(3),
							order: 2,
						},
					},
					"&.thumbnail-align-left:not(.thumbnail-size-full)": {
						"& .thumbnail-wrapper": {
							marginRight: theme.spacing(3),
							marginLeft: 0,
							order: 0,
						},
					},
					"&.thumbnail-position-before": {
						"& .thumbnail-wrapper": {
							marginBottom: theme.spacing(2),
						},
					},
					"&.is-global": {
						paddingBottom: theme.spacing(3),
						marginBottom: theme.spacing(3),
						borderBottom: `1px solid ${theme.palette.divider}`,
					},
					"& .excerpt-title, & .excerpt-summary": {
						"& em": {
							fontWeight: theme.typography.fontWeightBold,
							// color: theme.palette.primary.main,
							fontStyle: "normal",
							backgroundColor: "#fff5a1",
							// textDecoration: "underline",
						},
					},
				},
			}}
		>
			<Box
				sx={{
					height: "100%",
					...(mode !== "list" &&
						!flush && {
							boxShadow: `0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)`,
							backgroundColor: theme.palette.body.lighter,
							borderRadius: theme.shape.borderRadius,
						}),
					...(is_vcard &&
						vcard_align !== "left" && {
							padding: 0,
							paddingTop: theme.spacing(3),
						}),
					...(vcard_align === "left" && { padding: theme.spacing(2) }),
					"& .video-block": {
						boxShadow: "none !important",
					},
					"& figure.image": {
						margin: 0,
						"& .MuiPaper-root": {
							boxShadow: "none !important",
						},
					},
					...((type.includes("media--") || vcard_align === "left") && {
						display: "flex",
						justifyContent: "flex-start",
						alignItems: "center",
					}),
					...(vcard_align === "left" && {
						alignItems: "flex-start",
					}),
					...(mode === "list" &&
						(thumbnailSize === "md" || thumbnailSize === "sm") && {
							display: "flex",
							justifyContent: "flex-start",
							alignItems: "flex-start",
							...(thumbnailAlign === "right" && {
								justifyContent: "space-between",
							}),
							"& .thumbnail-wrapper": {
								marginRight: theme.spacing(3),
							},
						}),
				}}
			>
				{thumbnailPosition === "before" && <Thumb size={thumbnailSize} />}
				<Box
					className="excerpt-content"
					sx={{
						...(mode !== "list" &&
							!flush &&
							vcard_align !== "left" && { padding: theme.spacing(3) }),
						...(vcard_align === "left" && {
							paddingLeft: theme.spacing(2),
							width: "100%",
						}),
						"& > *": {
							":not(:first-child)": {
								marginTop: theme.spacing(0.5),
							},
						},
					}}
				>
					{datePosition === "before" && <Dt />}

					<Title
						component={headingComponent}
						variant={isSidebar ? "subtitle2" : "h4"}
						title={title}
						title_append={title_append}
						slug={slug}
						external={external}
						download={download}
						underline={underline}
						linkColor={linkColor}
						singleLine={type.includes("media--")}
						sx={{
							...(mode === "list" && { marginBottom: theme.spacing(1) }),
						}}
					/>

					{showType && matchingType && (
						<Typography
							sx={{
								backgroundColor: theme?.palette?.primary?.main,
								color: theme?.palette?.common?.white,
								padding: theme?.badges?.padding,
								borderRadius: theme?.badges?.radius,
								textTransform: theme?.badges?.textTransform,
								fontSize: theme?.badges?.fontSize,
								whiteSpace: "nowrap",
								width: "fit-content",
							}}
						>
							{matchingType.name}
						</Typography>
					)}

					{!hiddenInfo.includes("subtitle") &&
						thumbnailPosition !== "after" &&
						!isSidebar && (
							<Typography
								gutterBottom
								className="subtitle"
								dangerouslySetInnerHTML={{ __html: subtitle }}
							></Typography>
						)}
					{newBadge ? (
						<Badge
							badgeContent={"New"}
							sx={{ margin: "20px 0 0 18px" }}
							color="secondary"
						></Badge>
					) : null}
					{datePosition === "after" && <Dt />}
					{thumbnailPosition === "after" && <Thumb size={thumbnailSize} />}
					{!hiddenInfo.includes("subtitle") &&
						thumbnailPosition === "after" &&
						!isSidebar && (
							<Typography
								gutterBottom
								className="subtitle"
								dangerouslySetInnerHTML={{ __html: subtitle }}
							></Typography>
						)}
					{!is_vcard &&
						summary &&
						(!isSidebar ||
							(isSidebar && theme.customOptions.displaySidebarSummary) ||
							(isSidebar && config.options.displaySidebarSummary)) &&
						!hiddenInfo.includes("summary") && (
							<Typography
								variant="body2"
								className="excerpt-summary"
								dangerouslySetInnerHTML={{ __html: summary }}
							></Typography>
						)}
					{extra_attributes &&
						Object.keys(extra_attributes).map((attr) => {
							return props[attr] ? (
								<Tags
									label={extra_attributes[attr]}
									items={[{ title: props[attr] }]}
									inline
								/>
							) : (
								<></>
							);
						})}
					{!hiddenInfo.includes("tags") &&
						tags &&
						tags.map((group) => <Tags key={group.label} {...group} inline />)}

					{excerptLearnMore && (
						<Typography
							to={slug}
							component={Link}
							className="more-link"
							variant="body1"
							sx={{
								display: "block",
								color: theme.palette.link.main,
								paddingTop: theme.spacing(1),
								marginTop: theme.spacing(1),
								textDecoration: "none",
								textTransform: "uppercase",
								"&:hover, &:focus, &:active": {
									color: theme.palette.link.dark,
									textDecoration: "underline",
								},
							}}
						>
							Learn more
						</Typography>
					)}
				</Box>
			</Box>
		</Box>
	);
}

export default Excerpt;
